import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faDna, 
    faAngleDown, 
    faAngleRight, 
    faExclamationTriangle,
    faInfoCircle,
    faChartBar,
    faFlask
} from '@fortawesome/free-solid-svg-icons';
import './TranscriptomeCard.css';

/**
 * Specialized component for rendering Transcriptome Cell Population analysis data
 */
const TranscriptomeCard = ({ data, animationDelay }) => {
    // Debug logging
    console.log('Transcriptome Card received data:', data);
    
    const [expanded, setExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState('populations');
    
    // Early return if no data
    if (!data) {
        console.error('Transcriptome Card received no data');
        return null;
    }
    
    // Get status info for color elements
    const hasAbnormalCells = data.details?.summary?.abnormal_findings_count > 0;
    const statusColor = hasAbnormalCells ? '#ff9800' : '#64b5f6';
    const status = hasAbnormalCells ? 'Warning' : 'Positive';
    
    // Access the details object safely
    const details = data.details || {};
    const summary = details.summary || {};
    const cellPopulations = details.cell_population_analysis || [];
    
    // Calculate abnormal cell types
    const abnormalCellTypes = cellPopulations.filter(cell => 
        cell.status === 'Elevated' || cell.status === 'Decreased'
    );
    
    // Create summary information for display
    const summaries = [];
    
    if (abnormalCellTypes.length > 0) {
        summaries.push({
            text: `${abnormalCellTypes.length} abnormal cell population${abnormalCellTypes.length !== 1 ? 's' : ''}`,
            isWarning: true
        });
    }
    
    return (
        <div className="s2-transcript-card" style={{ animationDelay }}>
            <div className="s2-transcript-header" onClick={() => setExpanded(!expanded)}>
                <div className="s2-transcript-icon">
                    <FontAwesomeIcon 
                        icon={faDna} 
                        className="s2-transcript-icon-dna"
                    />
                </div>
                <div className="s2-transcript-title-container">
                    <div className="s2-transcript-title">Transcriptome Cell Population Analysis</div>

                    
                    {/* Cell Population Summary Section */}
                    {summaries.length > 0 && (
                        <div className="s2-transcript-summary">
                            {summaries.map((summary, index) => (
                                <div 
                                    key={index} 
                                    className={`s2-transcript-pill ${summary.isWarning ? 's2-transcript-pill-warning' : ''}`}
                                >
                                    {summary.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="s2-transcript-expand-icon">
                    <FontAwesomeIcon 
                        icon={expanded ? faAngleDown : faAngleRight} 
                    />
                </div>
            </div>
            
            {expanded && (
                <div className="s2-transcript-content s2-anim-fadeIn">
                    {/* WGS-style Summary Section */}
                    <div className="s2-transcript-wgs-summary">
                        <div className="s2-transcript-summary-row">
                            <div className="s2-transcript-summary-item">
                                <div className="s2-transcript-summary-label">Cell Types Analyzed</div>
                                <div className="s2-transcript-summary-value">
                                    {summary.cell_types_analyzed || 0} types
                                </div>
                            </div>
                            
                            <div className="s2-transcript-summary-item">
                                <div className="s2-transcript-summary-label">Abnormal Findings</div>
                                <div className="s2-transcript-summary-value">
                                    <span className={abnormalCellTypes.length > 0 ? 's2-transcript-negative' : ''}>
                                        {summary.abnormal_findings_count || 0} population{abnormalCellTypes.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Cell Population Analysis Section */}
                    <div className="s2-transcript-population-section">
                        <h3 className="s2-transcript-section-title">Cell Population Analysis</h3>
                        <div className="s2-transcript-population-grid">
                            {cellPopulations.map((cell, index) => {
                                // Calculate status color
                                const statusColorMap = {
                                    'Elevated': '#ff9800',
                                    'Decreased': '#f44336',
                                    'Normal': '#64b5f6'
                                };
                                
                                const cellStatusColor = cell.status === 'Normal' ? '#64b5f6' : statusColorMap[cell.status] || '#9e9e9e';
                                
                                // Extract range values
                                const rangeString = cell.healthy_range_percentage || '';
                                const [min, max] = rangeString.split(' - ').map(val => parseFloat(val));
                                
                                // Calculate percentage for visualization
                                const percentage = parseFloat(cell.patient_percentage);
                                const normalizedPercentage = Math.min(100, Math.max(0, ((percentage - min) / (max - min)) * 100));
                                
                                return (
                                    <div key={index} className="s2-transcript-cell-item">
                                        <div className="s2-transcript-cell-header">
                                            <div className="s2-transcript-cell-name">{cell.cell_type}</div>
                                            <div className="s2-transcript-cell-status" style={{ color: cellStatusColor }}>
                                                {cell.status}
                                            </div>
                                        </div>
                                        
                                        <div className="s2-transcript-cell-bar-container">
                                            <div className="s2-transcript-cell-range">
                                                <span className="s2-transcript-range-min">{min}%</span>
                                                <span className="s2-transcript-range-max">{max}%</span>
                                            </div>
                                            <div className="s2-transcript-range-bar">
                                                <div 
                                                    className="s2-transcript-patient-marker"
                                                    style={{ 
                                                        left: `${normalizedPercentage}%`,
                                                        backgroundColor: cellStatusColor
                                                    }}
                                                ></div>
                                            </div>
                                            <div className="s2-transcript-cell-value">
                                                Patient: <span style={{ fontWeight: 'bold' }}>{percentage}%</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    
                    {/* Fallback display if no data is available */}
                    {cellPopulations.length === 0 && (
                        <div className="s2-transcript-no-data">No cell population data available</div>
                    )}
                </div>
            )}
            
            {data.source && (
                <div className="s2-transcript-footer">
                    <span className="s2-transcript-source">
                        <FontAwesomeIcon icon={faInfoCircle} className="s2-transcript-source-icon" />
                        {data.source}
                    </span>
                </div>
            )}
        </div>
    );
};

export default TranscriptomeCard;
